import {defineContainer} from '@portabletext/editor'
import {defineBehavior, effect, forward, raise} from '@portabletext/editor/behaviors'
import {BehaviorPlugin, NodePlugin} from '@portabletext/editor/plugins'
import {CharacterPairDecoratorPlugin} from '@portabletext/plugin-character-pair-decorator'
import {Box, Button, Dialog} from '@sanity/ui'
import {useState} from 'react'
import {
  type ArrayOfObjectsInputProps,
  defineArrayMember,
  defineField,
  defineType,
  type PortableTextPluginsProps,
  useFormValue,
  useZIndex,
} from 'sanity'

const CONTAINER_NODES = [
  defineContainer({
    type: 'codeBlock',
    arrayField: 'code',
    render: ({children, attributes}) => (
      <pre
        {...attributes}
        style={{
          background: '#f6f6f6',
          border: '1px solid #ddd',
          borderRadius: 4,
          padding: '8px 12px',
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
        }}
      >
        {children}
      </pre>
    ),
  }),
]

// Keys come from the editor's own `keyGenerator` (off the behavior snapshot)
// so they honour any configured generator.
function emptyTextBlock(keyGenerator: () => string) {
  return {
    _key: keyGenerator(),
    _type: 'block',
    style: 'normal',
    markDefs: [],
    children: [{_key: keyGenerator(), _type: 'span', text: '', marks: []}],
  }
}

// The guard skips containers that already have content, which also stops the
// raise below from re-triggering.
const containerScaffoldBehaviors = [
  defineBehavior({
    on: 'insert.block',
    guard: ({event}) => {
      if (event.block._type !== 'codeBlock') {
        return false
      }
      const code = 'code' in event.block ? event.block.code : undefined
      return !(Array.isArray(code) && code.length > 0)
    },
    actions: [
      ({event, snapshot}) => [
        raise({
          ...event,
          block: {
            ...event.block,
            code: [emptyTextBlock(snapshot.context.keyGenerator)],
          },
        }),
      ],
    ],
  }),
]

function ContainerPlugins(props: PortableTextPluginsProps) {
  // Flips the same document between inline container rendering and
  // dialog-edited block objects; see the `containersEnabled` field description.
  const containersEnabled = useFormValue(['containersEnabled']) !== false

  return (
    <>
      {props.renderDefault({
        ...props,
        plugins: {
          ...props.plugins,
          table: {enabled: containersEnabled},
        },
      })}
      {containersEnabled ? (
        <>
          <NodePlugin nodes={CONTAINER_NODES} />
          <BehaviorPlugin behaviors={containerScaffoldBehaviors} />
        </>
      ) : null}
    </>
  )
}

// Binds the built-in table plugin to `sanity-plugin-rich-table`'s schema
// shape (`richTable` > `rows` > `row` > `cells` > `richTableCell` >
// `content`) via native `defineContainer` definitions: type and field
// names come from the containers, renders fall back to the studio's
// table UI. Module scope so the plugin doesn't re-register per render.
const richTableContainers = {
  table: defineContainer({type: 'richTable', arrayField: 'rows'}),
  row: defineContainer({type: 'row', arrayField: 'cells'}),
  cell: defineContainer({type: 'richTableCell', arrayField: 'content'}),
}

function RichTableAdoptionPlugins(props: PortableTextPluginsProps) {
  return props.renderDefault({
    ...props,
    plugins: {
      ...props.plugins,
      table: {enabled: true, containers: richTableContainers},
    },
  })
}

function TablePlugins(props: PortableTextPluginsProps) {
  return props.renderDefault({
    ...props,
    plugins: {...props.plugins, table: {enabled: true}},
  })
}

// Hosts the input in a dialog at Studio's own `inspectorDialog` offset, a
// layer that out-ranks the table's trash chips when they portal outside the
// editor.
function TableInDialogInput(props: ArrayOfObjectsInputProps) {
  const [open, setOpen] = useState(false)
  const zOffsets = useZIndex()

  return (
    <>
      <Button mode="ghost" onClick={() => setOpen(true)} text="Edit table in dialog" />
      {open && (
        <Dialog
          header="Table in dialog"
          id="table-in-dialog"
          onClose={() => setOpen(false)}
          width={2}
          zOffset={zOffsets.inspectorDialog}
        >
          <Box padding={4}>{props.renderDefault(props)}</Box>
        </Dialog>
      )}
    </>
  )
}

function tableCell(key: string, text: string) {
  return {
    _type: 'cell',
    _key: key,
    value: [
      {
        _type: 'block',
        _key: `${key}-block`,
        style: 'normal',
        markDefs: [],
        children: [{_type: 'span', _key: `${key}-span`, text, marks: []}],
      },
    ],
  }
}

export const customPlugins = defineType({
  name: 'customPlugins',
  title: 'Custom Plugins',
  type: 'document',
  fields: [
    {
      type: 'boolean',
      name: 'containersEnabled',
      title: 'Render containers inline',
      description:
        'When off, the Container Table field renders `table` and `codeBlock` as block objects edited through the dialog. Author a comment on nested text in one mode and toggle to verify it resolves in the other.',
      initialValue: true,
    },
    {
      type: 'array',
      name: 'containerTable',
      title: 'Container Table',
      description:
        'A defineContainer table (table > row > cell). Images and text blocks live at the root and inside cells. Cell blocks are deliberately narrower than root blocks (styles: normal/quote; decorators: strong/underline; lists: bullet; no annotations), so the toolbar disables the difference while the caret is inside a cell.',
      of: [
        defineArrayMember({
          type: 'block',
          of: [
            defineArrayMember({
              type: 'object',
              name: 'inlineNote',
              title: 'Inline note',
              fields: [defineField({type: 'string', name: 'text', title: 'Text'})],
              preview: {select: {title: 'text'}},
            }),
          ],
        }),
        defineArrayMember({
          type: 'image',
          options: {hotspot: true},
          fields: [defineField({name: 'alt', type: 'string', title: 'Alternative text'})],
        }),
        defineArrayMember({
          type: 'object',
          name: 'table',
          fields: [
            defineField({type: 'number', name: 'headerRows'}),
            defineField({
              type: 'array',
              name: 'rows',
              of: [
                defineArrayMember({
                  type: 'object',
                  name: 'row',
                  fields: [
                    defineField({
                      type: 'array',
                      name: 'cells',
                      of: [
                        defineArrayMember({
                          type: 'object',
                          name: 'cell',
                          fields: [
                            defineField({
                              type: 'array',
                              name: 'value',
                              of: [
                                defineArrayMember({
                                  type: 'block',
                                  // Deliberately narrower than the root
                                  // block in every toolbar-gated category
                                  // (styles, decorators, annotations,
                                  // lists), so the toolbar's positional
                                  // disabled state is easy to verify:
                                  // caret in a cell disables everything
                                  // the cell doesn't declare, caret at
                                  // the root enables it all again.
                                  styles: [
                                    {title: 'Normal', value: 'normal'},
                                    {title: 'Quote', value: 'blockquote'},
                                  ],
                                  marks: {
                                    decorators: [
                                      {title: 'Strong', value: 'strong'},
                                      {title: 'Underline', value: 'underline'},
                                    ],
                                    annotations: [],
                                  },
                                  lists: [{title: 'Bullet', value: 'bullet'}],
                                  of: [
                                    defineArrayMember({
                                      type: 'object',
                                      name: 'inlineNote',
                                      title: 'Inline note',
                                      fields: [
                                        defineField({type: 'string', name: 'text', title: 'Text'}),
                                      ],
                                      preview: {select: {title: 'text'}},
                                    }),
                                  ],
                                }),
                                defineArrayMember({
                                  type: 'image',
                                  options: {hotspot: true},
                                  fields: [
                                    defineField({
                                      name: 'alt',
                                      type: 'string',
                                      title: 'Alternative text',
                                    }),
                                  ],
                                }),
                              ],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
        defineArrayMember({
          type: 'object',
          name: 'codeBlock',
          title: 'Code block',
          fields: [
            defineField({
              type: 'array',
              name: 'code',
              of: [defineArrayMember({type: 'block', marks: {decorators: []}})],
            }),
          ],
        }),
      ],
      components: {
        portableText: {
          plugins: ContainerPlugins,
        },
      },
    },
    {
      type: 'array',
      name: 'richTableAdoption',
      title: 'Rich Table Adoption',
      description:
        "Built-in table editing bound to sanity-plugin-rich-table's schema shape (richTable > rows > row > cells > richTableCell > content) through the table plugin's containers config. The row's extra title field persists untouched; headerRows is the one field the built-in adds.",
      of: [
        defineArrayMember({type: 'block'}),
        defineArrayMember({
          type: 'object',
          name: 'richTable',
          fields: [
            // Not part of the rich-table shape: the built-in's header-row
            // toggle needs it declared or the value is stripped.
            defineField({type: 'number', name: 'headerRows'}),
            defineField({
              type: 'array',
              name: 'rows',
              of: [
                defineArrayMember({
                  type: 'object',
                  name: 'row',
                  fields: [
                    defineField({type: 'string', name: 'title'}),
                    defineField({
                      type: 'array',
                      name: 'cells',
                      of: [
                        defineArrayMember({
                          type: 'object',
                          name: 'richTableCell',
                          fields: [
                            defineField({
                              type: 'array',
                              name: 'content',
                              of: [defineArrayMember({type: 'block'})],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
      components: {
        portableText: {
          plugins: RichTableAdoptionPlugins,
        },
      },
    },
    {
      type: 'array',
      name: 'tableInDialog',
      title: 'Table in Dialog',
      description:
        'A table-enabled input inside a dialog layer at Studio\'s inspectorDialog offset. Click "Edit table in dialog", hover a cell in the first column and click the column handle above it: a trash button appears above the handle. Click it to delete the column. Broken: the trash button paints beneath the dialog, so it is hidden and clicking where it should be does nothing. Fixed: it shows above the handle and deletes the column. The row handle and its trash button on the left behave the same.',
      initialValue: [
        {
          _type: 'table',
          _key: 'table',
          headerRows: 0,
          rows: [
            {_type: 'row', _key: 'row-1', cells: [tableCell('a1', 'A1'), tableCell('b1', 'B1')]},
            {_type: 'row', _key: 'row-2', cells: [tableCell('a2', 'A2'), tableCell('b2', 'B2')]},
          ],
        },
      ],
      of: [
        defineArrayMember({type: 'block'}),
        defineArrayMember({
          type: 'object',
          name: 'table',
          fields: [
            defineField({type: 'number', name: 'headerRows'}),
            defineField({
              type: 'array',
              name: 'rows',
              of: [
                defineArrayMember({
                  type: 'object',
                  name: 'row',
                  fields: [
                    defineField({
                      type: 'array',
                      name: 'cells',
                      of: [
                        defineArrayMember({
                          type: 'object',
                          name: 'cell',
                          fields: [
                            defineField({
                              type: 'array',
                              name: 'value',
                              of: [defineArrayMember({type: 'block'})],
                            }),
                          ],
                        }),
                      ],
                    }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
      components: {
        input: TableInDialogInput,
        portableText: {
          plugins: TablePlugins,
        },
      },
    },
    {
      type: 'string',
      name: 'title',
    },

    /**
     * One-Line Editor
     *
     * Uses the `OneLinePlugin` to restrict the editor to one line of text.
     */
    {
      type: 'array',
      name: 'oneLineEditor',
      title: 'One-Line Editor',
      description: 'The editor is restricted to one line of text using the <OneLinePlugin />',
      of: [
        defineArrayMember({
          type: 'block',
          options: {
            oneLine: true,
          },
        }),
      ],
    },

    /**
     * Custom Markdown Config
     *
     * Uses `components.portableText.plugins` to reconfigure the `MarkdownShortcutsPlugin`.
     */
    {
      type: 'array',
      name: 'customMarkdownConfig',
      title: 'Custom Markdown Config',
      description:
        'Only a "bold" decorator is allowed and the unordered list is called "dot", and the <MarkdownShortcutsPlugin /> has been reconfigured to support this',
      of: [
        {
          type: 'block',
          marks: {
            decorators: [
              {
                value: 'bold',
                title: 'Bold',
                component: ({children}) => <strong>{children}</strong>,
              },
            ],
          },
          lists: [
            {
              value: 'dot',
              title: 'Dot',
            },
          ],
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                markdown: {
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  boldDecorator: ({schema}) =>
                    schema.decorators.find((decorator) => decorator.name === 'bold')?.name,
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  unorderedList: ({schema}) =>
                    schema.lists.find((list) => list.name === 'dot')?.name,
                },
              },
            })
          },
        },
      },
    },

    /**
     * Custom Markdown Config (the deprecated way)
     *
     * Uses `components.portableText.plugins` to reconfigure the `MarkdownShortcutsPlugin`.
     */
    {
      type: 'array',
      name: 'customMarkdownConfigDeprecated',
      title: 'Custom Markdown Config (the deprecated way)',
      description:
        'Only a "bold" decorator is allowed and the unordered list is called "dot", and the <MarkdownShortcutsPlugin /> has been reconfigured to support this',
      of: [
        {
          type: 'block',
          marks: {
            decorators: [
              {
                value: 'bold',
                title: 'Bold',
                component: ({children}) => <strong>{children}</strong>,
              },
            ],
          },
          lists: [
            {
              value: 'dot',
              title: 'Dot',
            },
          ],
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                markdown: {
                  config: {
                    // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                    boldDecorator: ({schema}) =>
                      schema.decorators.find((decorator) => decorator.name === 'bold')?.name,
                    // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                    unorderedListStyle: ({schema}) =>
                      schema.lists.find((list) => list.name === 'dot')?.name,
                  },
                },
              },
            })
          },
        },
      },
    },

    /**
     * Markdown Shortcuts Disabled
     */
    {
      type: 'array',
      name: 'markdownShortcutsDisabled',
      title: 'Markdown Shortcuts Disabled',
      description: 'The markdown shortcuts are disabled',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                markdown: {
                  enabled: false,
                },
              },
            })
          },
        },
      },
    },

    /**
     * Custom Decorator Shortcuts
     *
     * Uses the `CharacterPairDecoratorPlugin` add custom decorator shortcuts in the
     * editor.
     */
    {
      type: 'array',
      name: 'customDecoratorShortcuts',
      title: 'Custom Decorator Shortcuts',
      description: 'The markdown shortcuts for bold and italic have been replaced with 👻 and 🕹️',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return (
              <>
                {props.renderDefault({
                  ...props,
                  plugins: {
                    ...props.plugins,
                    markdown: {
                      config: {
                        ...props.plugins.markdown,
                        boldDecorator: undefined,
                        italicDecorator: undefined,
                      },
                    },
                  },
                })}
                <CharacterPairDecoratorPlugin
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  decorator={({schema}) =>
                    schema.decorators.find((decorator) => decorator.name === 'strong')?.name
                  }
                  pair={{
                    char: '👻',
                    amount: 2,
                  }}
                />
                <CharacterPairDecoratorPlugin
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  decorator={({schema}) =>
                    schema.decorators.find((decorator) => decorator.name === 'strong')?.name
                  }
                  pair={{
                    char: '🕹️',
                    amount: 2,
                  }}
                />
                <CharacterPairDecoratorPlugin
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  decorator={({schema}) =>
                    schema.decorators.find((decorator) => decorator.name === 'em')?.name
                  }
                  pair={{
                    char: '👻',
                    amount: 1,
                  }}
                />
                <CharacterPairDecoratorPlugin
                  // oxlint-disable-next-line no-deprecated -- will fix in follow up PR
                  decorator={({schema}) =>
                    schema.decorators.find((decorator) => decorator.name === 'em')?.name
                  }
                  pair={{
                    char: '🕹️',
                    amount: 1,
                  }}
                />
              </>
            )
          },
        },
      },
    },

    /**
     * All Typographic rules enabled
     */
    {
      type: 'array',
      name: 'allTypographicRulesEnabled',
      title: 'All Typographic Rules Enabled',
      description: 'All typographic rules are enabled',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                typography: {
                  ...props.plugins.typography,
                  preset: 'all',
                },
              },
            })
          },
        },
      },
    },

    /**
     * No Typographic rules enabled
     */
    {
      type: 'array',
      name: 'noTypographicRulesEnabled',
      title: 'No Typographic Rules Enabled',
      description: 'No typographic rules are enabled',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                typography: {
                  enabled: false,
                },
              },
            })
          },
        },
      },
    },

    /**
     * Paste Link Disabled
     *
     * Disables the default behavior of converting selected text into a link
     * when a URL is pasted.
     */
    {
      type: 'array',
      name: 'pasteLinkDisabled',
      title: 'Paste Link Disabled',
      description:
        'The paste link behavior is disabled - pasting a URL on selected text will replace the text',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                pasteLink: {
                  enabled: false,
                },
              },
            })
          },
        },
      },
    },

    /**
     * Custom Paste Link Annotation
     *
     * Uses a custom link matcher to support a different annotation type
     * for links (e.g., 'customLink' instead of 'link').
     */
    {
      type: 'array',
      name: 'customPasteLinkAnnotation',
      title: 'Custom Paste Link Annotation',
      description:
        'The paste link behavior uses a custom annotation called "customLink" with a "url" field instead of the default "link" with "href"',
      of: [
        {
          type: 'block',
          marks: {
            annotations: [
              {
                name: 'customLink',
                title: 'Custom Link',
                type: 'object',
                fields: [
                  {
                    name: 'url',
                    title: 'URL',
                    type: 'url',
                  },
                ],
              },
            ],
          },
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return props.renderDefault({
              ...props,
              plugins: {
                ...props.plugins,
                pasteLink: {
                  link: ({context, value}) => {
                    const customLink = context.schema.annotations.find(
                      (a) => a.name === 'customLink',
                    )
                    if (!customLink) return undefined
                    return {_type: 'customLink', url: value.href}
                  },
                },
              },
            })
          },
        },
      },
    },

    /**
     * No Plugins
     *
     * Removes all plugins from the editor.
     */
    {
      type: 'array',
      name: 'noPlugins',
      title: 'No Plugins',
      description: 'All plugins are removed, even the default ones',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: () => {
            return null
          },
        },
      },
    },

    /**
     * Custom Behavior
     *
     * Uses the `BehaviorPlugin` to add a custom behavior to the editor
     * without much boilerplate.
     */
    {
      type: 'array',
      name: 'customBehavior',
      title: 'Custom Behavior',
      description: 'Custom "log text insertions" Behavior using the <BehaviorPlugin />',
      of: [
        {
          type: 'block',
        },
      ],
      components: {
        portableText: {
          plugins: (props) => {
            return (
              <>
                {props.renderDefault(props)}
                <BehaviorPlugin
                  behaviors={[
                    defineBehavior({
                      on: 'insert.text',
                      actions: [
                        ({event}) => [
                          effect(() => {
                            console.log(event)
                          }),
                          forward(event),
                        ],
                      ],
                    }),
                  ]}
                />
              </>
            )
          },
        },
      },
    },
  ],
})
